import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Download, Sparkles, Trash2, Users, MapPin } from "lucide-react";
import { DataCleanupTable } from "./DataCleanupTable";

interface CleanedRecord {
  tax_id: string;
  date_recorded: string;
  instrument_type: string;
  county: string;
  prefix: string;
  original_name: string;
  first_name: string;
  last_name: string;
  seller_address: string;
  seller_city: string;
  seller_state: string;
  seller_zip: string;
  amount: string;
  original_data?: string;
  issues_found?: string[];
}

export default function DataCleanupPage() {
  const [rawData, setRawData] = useState("");
  const [cleanedData, setCleanedData] = useState<CleanedRecord[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const { toast } = useToast();

  const parseTabDelimitedData = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split('\t').map(h => h.trim().toLowerCase().replace(/ /g, '_'));
    const records = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      if (values.length === headers.length) {
        const record: any = {};
        headers.forEach((header, idx) => {
          record[header] = values[idx]?.trim() || '';
        });
        records.push(record);
      }
    }

    return records;
  };

  const cleanupData = async () => {
    if (!rawData.trim()) {
      toast({ title: "No data provided", description: "Please paste your data first", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    setStatusMessage("Parsing your data...");
    setCleanedData([]);
    try {
      const parsedRecords = parseTabDelimitedData(rawData);
      
      if (parsedRecords.length === 0) {
        toast({ title: "Parse Error", description: "Could not parse the data. Make sure it's tab-delimited.", variant: "destructive" });
        setIsProcessing(false);
        setStatusMessage("");
        return;
      }

      // Process in small client-side batches to avoid edge function timeouts
      const BATCH_SIZE = 25;
      const totalBatches = Math.ceil(parsedRecords.length / BATCH_SIZE);
      const allCleaned: CleanedRecord[] = [];

      for (let i = 0; i < parsedRecords.length; i += BATCH_SIZE) {
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;
        const batch = parsedRecords.slice(i, i + BATCH_SIZE);
        setStatusMessage(`Processing batch ${batchNum} of ${totalBatches} (${allCleaned.length} records cleaned so far)...`);

        const { data, error } = await supabase.functions.invoke('cleanup-mailing-data', {
          body: { records: batch }
        });

        if (error) throw error;
        
        const batchCleaned = data.cleaned_records || [];
        allCleaned.push(...batchCleaned);
        
        // Update UI progressively
        setCleanedData([...allCleaned]);
      }

      setStatusMessage(`Done! ${allCleaned.length} records cleaned. Review them below.`);
      toast({ title: "Data Cleaned!", description: `Processed ${allCleaned.length} records with AI assistance` });

      setTimeout(() => {
        document.getElementById('cleaned-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Cleanup error:', error);
      setStatusMessage("Cleanup failed — see error details below.");
      toast({
        title: "Cleanup Failed",
        description: error instanceof Error ? error.message : "Failed to clean data. Try a smaller batch.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const removeDuplicates = () => {
    const seen = new Map<string, number>();
    const unique: CleanedRecord[] = [];
    let dupeCount = 0;

    for (const record of cleanedData) {
      const key = `${(record.first_name || '').toLowerCase().trim()}|${(record.last_name || '').toLowerCase().trim()}|${(record.seller_address || '').toLowerCase().trim()}`;
      if (!seen.has(key)) {
        seen.set(key, 1);
        unique.push(record);
      } else {
        dupeCount++;
      }
    }

    setCleanedData(unique);
    toast({ title: "Duplicates Removed", description: `Removed ${dupeCount} duplicate records. ${unique.length} unique records remain.` });
  };

  // Common female names for gender detection
  const FEMALE_NAMES = new Set([
    'mary','patricia','jennifer','linda','barbara','elizabeth','susan','jessica','sarah','karen',
    'nancy','lisa','betty','margaret','sandra','ashley','dorothy','kimberly','emily','donna',
    'michelle','carol','amanda','melissa','deborah','stephanie','rebecca','sharon','laura','cynthia',
    'kathleen','amy','angela','shirley','anna','brenda','pamela','emma','nicole','helen','samantha',
    'katherine','christine','debra','rachel','carolyn','janet','catherine','maria','heather','diane',
    'ruth','julie','olivia','joyce','virginia','victoria','kelly','lauren','christina','joan',
    'evelyn','judith','megan','andrea','cheryl','hannah','jacqueline','martha','gloria','teresa',
    'ann','sara','madison','frances','kathryn','janice','jean','abigail','alice','judy','sophia',
    'grace','denise','amber','doris','marilyn','danielle','beverly','isabella','theresa','diana',
    'natalie','brittany','charlotte','marie','kayla','alexis','lori','tammy','colleen','jeanette',
    'marlene','norma','lynn','elsa','eileen','allison','candace','joann','regina','phyllis',
    'hattie','elaine','rita','ruby','agnes','miriam','ellen','gail','kate',
    'cindy','sally','mabel','jane','sue','marcia','anne','becky','inez','rosemarie','sherri',
    'connie','delores','geraldine','hazel','jetta','phoebe','nan','dianne',
    'jodi','tammi','joan','doris','cathy','edith','scarlett','amelia',
    'jody','rebekah','kari','buffilyn','leca','gidget','denisa'
  ]);

  const MALE_NAMES = new Set([
    'james','robert','john','michael','david','william','richard','joseph','thomas','charles',
    'christopher','daniel','matthew','anthony','mark','donald','steven','paul','andrew','joshua',
    'kenneth','kevin','brian','george','timothy','ronald','edward','jason','jeffrey','ryan',
    'jacob','gary','nicholas','eric','jonathan','stephen','larry','justin','scott','brandon',
    'benjamin','samuel','raymond','gregory','frank','alexander','patrick','jack','dennis','jerry',
    'tyler','aaron','jose','adam','nathan','henry','peter','zachary','douglas','harold',
    'carl','arthur','gerald','roger','keith','ralph','lawrence','albert','joe','terry',
    'sean','troy','barry','floyd','bobby','billy','bruce','eugene','russell','elijah','walter',
    'leeman','homer','dewey','jorge','joel','warren','orville','lloyd','craig','danny','gene',
    'fred','kirk','butch','phillip','wilburn','dallas','brett','lee','roy','clyde','jimmy',
    'everardo','normand','glenn','boyd','preston','royce','juan','mikhail','allen','otis',
    'holland','rick','sam','calvin','shawn','albert','brad'
  ]);

  const isFemale = (name: string) => FEMALE_NAMES.has(name.toLowerCase().trim());
  const isMale = (name: string) => MALE_NAMES.has(name.toLowerCase().trim());

  const mergeCouples = () => {
    const addressGroups = new Map<string, { index: number; record: CleanedRecord }[]>();
    
    cleanedData.forEach((record, index) => {
      const addr = (record.seller_address || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
      if (!addr) return;
      
      const key = `${addr}|${(record.seller_city || '').toLowerCase().trim()}|${(record.seller_state || '').toLowerCase().trim()}`;
      if (!addressGroups.has(key)) {
        addressGroups.set(key, []);
      }
      addressGroups.get(key)!.push({ index, record });
    });

    const indicesToRemove = new Set<number>();
    let mergeCount = 0;
    const newData = [...cleanedData];

    for (const [, group] of addressGroups) {
      if (group.length !== 2) continue;
      
      const [a, b] = group;
      const sameLastName = (a.record.last_name || '').toLowerCase().trim() === (b.record.last_name || '').toLowerCase().trim();
      if (!sameLastName) continue;

      const aFirstName = (a.record.first_name || '').split(' ')[0];
      const bFirstName = (b.record.first_name || '').split(' ')[0];

      let husbandIdx: number;
      let wifeIdx: number;

      if (isMale(aFirstName) && isFemale(bFirstName)) {
        husbandIdx = a.index;
        wifeIdx = b.index;
      } else if (isFemale(aFirstName) && isMale(bFirstName)) {
        husbandIdx = b.index;
        wifeIdx = a.index;
      } else {
        continue;
      }

      newData[husbandIdx] = { ...newData[husbandIdx], prefix: 'Mr. and Mrs.' };
      indicesToRemove.add(wifeIdx);
      mergeCount++;
    }

    const merged = newData.filter((_, i) => !indicesToRemove.has(i));
    setCleanedData(merged);
    toast({ 
      title: "Couples Merged", 
      description: `Merged ${mergeCount} couples into "Mr. and Mrs." records. ${indicesToRemove.size} duplicate rows removed.` 
    });
  };

  const findAddressDuplicates = () => {
    const addressGroups = new Map<string, { index: number; record: CleanedRecord }[]>();
    
    cleanedData.forEach((record, index) => {
      const addr = (record.seller_address || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
      if (!addr) return;
      
      const key = `${addr}|${(record.seller_city || '').toLowerCase().trim()}|${(record.seller_state || '').toLowerCase().trim()}`;
      if (!addressGroups.has(key)) {
        addressGroups.set(key, []);
      }
      addressGroups.get(key)!.push({ index, record });
    });

    const indicesToRemove = new Set<number>();
    let dupeCount = 0;

    for (const [, group] of addressGroups) {
      if (group.length < 2) continue;
      
      // Keep the first record, mark the rest for removal
      for (let i = 1; i < group.length; i++) {
        indicesToRemove.add(group[i].index);
        dupeCount++;
      }
    }

    if (dupeCount === 0) {
      toast({ title: "No Address Duplicates", description: "No records share the same address." });
      return;
    }

    const deduped = cleanedData.filter((_, i) => !indicesToRemove.has(i));
    setCleanedData(deduped);
    toast({ 
      title: "Address Duplicates Removed", 
      description: `Removed ${dupeCount} records sharing an address with another record. ${deduped.length} remain.` 
    });
  };

  const exportToCSV = () => {
    if (cleanedData.length === 0) return;

    const headers = ['Tax ID', 'Date Recorded', 'Instrument Type', 'County', 'Prefix', 'First Name', 'Last Name',
                     'Seller Address', 'Seller City', 'Seller State', 'Seller Zip', 'Amount'];
    
    const csvContent = [
      headers.join(','),
      ...cleanedData.map(record => [
        record.tax_id,
        record.date_recorded,
        record.instrument_type,
        record.county,
        `"${record.prefix || ''}"`,
        `"${record.first_name}"`,
        `"${record.last_name}"`,
        `"${record.seller_address}"`,
        record.seller_city,
        record.seller_state,
        record.seller_zip,
        `"${record.amount || ''}"`,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cleaned-mailing-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Your cleaned data has been downloaded",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Data Cleanup Tool</h1>
        <p className="text-muted-foreground mt-2">
          Paste messy spreadsheet data and let AI clean it up for Melissa Data
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Paste Raw Data</CardTitle>
          <CardDescription>
            Copy and paste data directly from Excel or Google Sheets (tab-delimited format will be preserved)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Paste your tab-delimited data here..."
            value={rawData}
            onChange={(e) => setRawData(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
          />
          
          <div className="flex gap-2">
            <Button 
              onClick={cleanupData} 
              disabled={isProcessing || !rawData.trim()}
              className="flex-1"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Clean Up Data with AI
                </>
              )}
            </Button>
            {(rawData.trim() || cleanedData.length > 0) && (
              <Button
                variant="outline"
                onClick={() => { setRawData(""); setCleanedData([]); setStatusMessage(""); }}
                disabled={isProcessing}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          {statusMessage && (
            <div className={`text-sm p-3 rounded-md ${cleanedData.length > 0 ? 'bg-green-500/10 text-green-700 dark:text-green-400' : isProcessing ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400' : 'bg-destructive/10 text-destructive'}`}>
              {isProcessing && <Loader2 className="inline mr-2 h-3 w-3 animate-spin" />}
              {statusMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {cleanedData.length > 0 && (
        <Card id="cleaned-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Cleaned Data ({cleanedData.length} records)</CardTitle>
                <CardDescription>
                  Review the cleaned data below. You can edit any field before exporting.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={mergeCouples} variant="secondary">
                  <Users className="mr-2 h-4 w-4" />
                  Merge Couples
                </Button>
                <Button onClick={findAddressDuplicates} variant="secondary">
                  <MapPin className="mr-2 h-4 w-4" />
                  Dedupe by Address
                </Button>
                <Button onClick={removeDuplicates} variant="secondary">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove Duplicates
                </Button>
                <Button onClick={exportToCSV} variant="outline">
                  <Download className="mr-2 h-4 w-4" />
                  Export to CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <DataCleanupTable 
              data={cleanedData} 
              onDataChange={setCleanedData}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
